import React, { useState, useEffect, useRef } from "react";
import ProcessHeader from "./components/ProcessHeader";
import ProcessStepGroup from "./components/ProcessStepGroup";
import ProcessDetail from "./components/ProcessDetail";
import { processAll, process1, process2, process3 } from "./components/processData";
import bgImage from "../../../images/main/main8.jpg";
import { MdPlayArrow, MdPause } from "react-icons/md";

import '../../../custom/swiper.css';

const ProcessPage = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isAutoPlay, setIsAutoPlay] = useState(true);
    const [isPaused, setIsPaused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const intervalRef = useRef(null);

    const swiperCtrl = (index) => {
        setActiveIndex(index);
        // 수동 조작 시 잠시 자동 진행 일시정지
        setIsPaused(true);
        setTimeout(() => setIsPaused(false), 3000); // 3초 후 자동 진행 재개
    };

    // 마우스 호버 시 멈춤
    const handleMouseEnter = () => {
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
    };

    // 키보드 제어 기능
    useEffect(() => {
        const handleKeyDown = (e) => {
            switch(e.key) {
                case 'ArrowRight':
                case ' ': // 스페이스바
                    e.preventDefault();
                    swiperCtrl(activeIndex === processAll.length - 1 ? 0 : activeIndex + 1);
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    swiperCtrl(activeIndex === 0 ? processAll.length - 1 : activeIndex - 1);
                    break;
                case 'Escape':
                    e.preventDefault();
                    setIsAutoPlay(false);
                    break;
                case 'Enter':
                    e.preventDefault();
                    setIsAutoPlay(!isAutoPlay);
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [activeIndex, processAll.length, isAutoPlay]);

    // 자동 진행 기능
    useEffect(() => {
        if (isAutoPlay && !isPaused && !isHovered) {
            intervalRef.current = setInterval(() => {
                setActiveIndex(prevIndex => {
                    const nextIndex = prevIndex + 1;
                    return nextIndex >= processAll.length ? 0 : nextIndex; // 마지막에서 처음으로
                });
            }, 4000); // 4초마다 변경
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isAutoPlay, isPaused, isHovered]);

    const toggleAutoPlay = () => {
        setIsAutoPlay(!isAutoPlay);
        setIsPaused(false);
    };

    return (
        <div
            className="w-full h-screen bg-no-repeat bg-cover relative"
            style={{ backgroundImage: `url(${bgImage})`, margin: 0, padding: 0 }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {/* 자동 재생 제어 버튼 */}
            <button
                onClick={toggleAutoPlay}
                className="absolute top-4 right-4 z-50 w-12 h-12 bg-white/90 backdrop-blur-md 
                         rounded-full flex items-center justify-center shadow-lg border border-gray-200/50
                         hover:bg-white hover:shadow-xl transition-all duration-300 group"
                title={isAutoPlay ? "자동 재생 정지" : "자동 재생 시작"}
            >
                {isAutoPlay ? (
                    <MdPause size={24} className="text-gray-700 group-hover:text-red-600 transition-colors duration-300" />
                ) : (
                    <MdPlayArrow size={24} className="text-gray-700 group-hover:text-green-600 transition-colors duration-300" />
                )}
            </button>

            {/* 진행 상태 표시 */}
            <div className="absolute top-4 left-4 z-50 flex items-center space-x-2">
                <div className="bg-white/90 backdrop-blur-md rounded-full px-4 py-2 shadow-lg border border-gray-200/50">
                    <span className="text-sm font-medium text-gray-700">
                        {activeIndex + 1} / {processAll.length}
                    </span>
                </div>
                {isAutoPlay && !isPaused && !isHovered && (
                    <div className="bg-green-500 w-3 h-3 rounded-full animate-pulse shadow-lg" title="자동 재생 중"></div>
                )}
                {isHovered && (
                    <div className="bg-orange-500 w-3 h-3 rounded-full shadow-lg" title="마우스 호버로 일시정지"></div>
                )}
                {isPaused && (
                    <div className="bg-blue-500 w-3 h-3 rounded-full shadow-lg" title="일시정지 (3초 후 재개)"></div>
                )}
            </div>

            {/* 키보드 컨트롤 안내 */}
            <div className="absolute bottom-4 left-4 z-50">
                <div className="bg-black/70 backdrop-blur-md rounded-lg px-3 py-2 shadow-lg text-white text-xs space-y-1">
                    <div>키보드 조작: ← → Space Bar</div>
                    <div>Enter: 재생/정지 | ESC: 정지</div>
                    <div>마우스 호버: 일시정지</div>
                </div>
            </div>

            <div className="flex flex-col h-screen lg:flex-row justify-center">
                <div className="h-auto lg:w-[60%] lg:h-screen flex flex-col lg:flex-row -mt-20 lg:mt-0 items-center lg:justify-end mx-5 lg:mb-0 lg:p-0">
                    <div className="lg:space-y-[50px]">
                        <ProcessHeader />

                        <div className="hidden lg:block space-y-4">
                            <ProcessStepGroup 
                                partNumber={1}
                                steps={process1}
                                activeIndex={activeIndex}
                                onStepClick={swiperCtrl}
                            />
                            
                            <ProcessStepGroup 
                                partNumber={2}
                                steps={process2}
                                activeIndex={activeIndex}
                                onStepClick={swiperCtrl}
                            />
                            
                            <ProcessStepGroup 
                                partNumber={3}
                                steps={process3}
                                activeIndex={activeIndex}
                                onStepClick={swiperCtrl}
                            />
                        </div>
                    </div>
                </div>
                
                <div className="w-full lg:w-[40%] lg:h-screen flex items-center p-3 lg:ps-[5%] pt-[2%]">
                    <ProcessDetail 
                        processData={processAll}
                        activeIndex={activeIndex}
                        onIndexChange={swiperCtrl}
                    />
                </div>
            </div>  
        </div>
    );
};

export default ProcessPage;